import { get } from 'lodash';
import { generateUUID } from '~/helpers/util/generateUUID';

type WithKey<T> = T & { key: string };

/**
 * Adiciona uma key única (UUID) a um único item
 */
const addKeyToItem = <T extends object>(item: T): WithKey<T> => ({
  ...item,
  key: generateUUID()
});

/**
 * Adiciona uma key única (UUID) a cada item de uma lista
 * @param items Lista de itens que precisam de keys únicas
 * @returns Nova lista com os itens originais + key única
 */
export const withListKeys = <T extends object>(items: T[] = []): WithKey<T>[] =>
  items.map(addKeyToItem);

/**
 * Adiciona uma key única (UUID) a cada item de uma lista e seus itens aninhados
 * @param data Lista de itens que precisam de keys únicas
 * @param childKey Nome da propriedade que contém a lista aninhada
 * @returns Nova lista com os itens originais + key única, incluindo os itens aninhados
 */
export const withNestedListKeys = <T extends object>(
  items: T[] = [],
  childKey: string
): WithKey<T>[] => {
  const addKeyToNestedItem = (item: T): WithKey<T> => {
    const children = get(item, childKey, []);
    return {
      ...addKeyToItem(item),
      [childKey]: withListKeys(children)
    };
  };

  return items.map(addKeyToNestedItem);
};
